import type { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { InfoTip } from '@/features/base-garments/components/InfoTip';
import { plainTipText } from '@/features/base-garments/lib/base-garments-tips';

/**
 * The (i) tips on the 3PL usage tab. Their copy restates rules the bill is
 * built on, so change it with them:
 * - `chart`: a piece counts once its order is SHIPPED or READY_FOR_PICKUP
 *   (migration 20261010150000).
 * - `printing`, `ownerStock`: `buildBill` (`lib/three-pl-usage-view.ts:724`).
 *   Printing is printed pieces shipped; Pick and pack is every piece shipped;
 *   a garment of the 3PL's (`substituted_sku_id`) is priced per SKU.
 * - `notShipped`: printed-then-cancelled charges printing, plus the 3PL's
 *   garment once printed on; an artwork file error charges printing, plus the
 *   3PL's garment; production redos are never billed (migration 20261012023000).
 * - `price`: the price in force on the charge's day; the first price fills
 *   the days before it (migration 20261011150000).
 */
export type ThreePlUsageTipName = 'chart' | 'printing' | 'ownerStock' | 'notShipped' | 'price';

const TIP_COMPONENTS: Record<string, ReactElement> = {
  b: <strong className="font-semibold" />,
  br: <br />,
};

export interface ThreePlUsageTipProps {
  name: ThreePlUsageTipName;
  /** `owner`: the 3PL's name, for the tips that name it. */
  values?: Record<string, string | number>;
  align?: 'start' | 'center' | 'end';
  className?: string;
}

/** An (i) with one of the 3PL usage tips: title, then the body. Mirrors `BaseGarmentTip`. */
export function ThreePlUsageTip({ name, values, align, className }: ThreePlUsageTipProps) {
  const { t } = useTranslation('inventory');
  // The keys are written out at each call (not held in a variable), so `check:i18n` sees the tips family.
  const title = t(`reporting.threePlUsage.tips.${name}.title`, values);
  const body = t(`reporting.threePlUsage.tips.${name}.body`, values);
  return (
    <InfoTip label={plainTipText(`${title}: ${body}`)} align={align} className={className}>
      <p className="font-semibold">{title}</p>
      <div className="mt-0.5">
        <Trans t={t} i18nKey={`reporting.threePlUsage.tips.${name}.body`} values={values} components={TIP_COMPONENTS} />
      </div>
    </InfoTip>
  );
}
